import React, { useEffect, useState } from 'react';
import {
    Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    FormControlLabel, Radio, RadioGroup, TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppraisalService } from '../../services/appraisal.service';
import { DepartmentService } from '../../services/department.service';
import { useToast } from '../../context/ToastContext';
import { Department } from '../../interfaces/department.interface';
import { IAddParticipantsRequest } from '../../interfaces/appraisal.interface';
import { MultiUserPicker, UserOption } from './UserPicker';

type Mode = 'all' | 'departments' | 'people';

interface Props {
    open: boolean;
    institutionId: string;
    cycleId: string;
    onClose: () => void;
    onAdded: () => void;
}

const AddParticipantsDialog: React.FC<Props> = ({ open, institutionId, cycleId, onClose, onAdded }) => {
    const toast = useToast();
    const { t } = useTranslation(['appraisal', 'common']);
    const [mode, setMode] = useState<Mode>('all');
    const [departments, setDepartments] = useState<Department[]>([]);
    const [pickedDepartments, setPickedDepartments] = useState<Department[]>([]);
    const [people, setPeople] = useState<UserOption[]>([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setMode('all');
        setPickedDepartments([]);
        setPeople([]);
        (async () => {
            try {
                const res = await DepartmentService.getDepartmentsByInstitution();
                setDepartments(res?.content ?? []);
            } catch (err) {
                toast.showApiError(err, t('participants.addDialog.errors.departments'));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const add = async () => {
        let request: IAddParticipantsRequest;
        if (mode === 'all') {
            request = { all: true };
        } else if (mode === 'departments') {
            if (pickedDepartments.length === 0) {
                toast.showError(t('participants.addDialog.errors.pickDepartment'));
                return;
            }
            request = { departmentIds: pickedDepartments.map(d => d.id) };
        } else {
            if (people.length === 0) {
                toast.showError(t('participants.addDialog.errors.pickPerson'));
                return;
            }
            request = { userIds: people.map(p => p.id) };
        }
        setBusy(true);
        try {
            const added = await AppraisalService.addParticipants(institutionId, cycleId, request);
            toast.showSuccess(t('participants.addDialog.added', { count: added.length }));
            onAdded();
            onClose();
        } catch (err) {
            toast.showApiError(err, t('participants.addDialog.errors.add'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('participants.addDialog.title')}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 1 }}>{t('participants.addDialog.help')}</DialogContentText>
                <RadioGroup value={mode} onChange={e => setMode(e.target.value as Mode)}>
                    <FormControlLabel value="all" control={<Radio />} label={t('participants.addDialog.mode.all')} />
                    <FormControlLabel value="departments" control={<Radio />} label={t('participants.addDialog.mode.departments')} />
                    <FormControlLabel value="people" control={<Radio />} label={t('participants.addDialog.mode.people')} />
                </RadioGroup>
                {mode === 'departments' && (
                    <Autocomplete
                        multiple
                        sx={{ mt: 1 }}
                        options={departments}
                        value={pickedDepartments}
                        onChange={(_, v) => setPickedDepartments(v)}
                        getOptionLabel={d => d.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={params => <TextField {...params} label={t('participants.addDialog.departments')} />}
                    />
                )}
                {mode === 'people' && (
                    <MultiUserPicker
                        institutionId={institutionId}
                        value={people}
                        onChange={setPeople}
                        label={t('participants.addDialog.people')}
                        helperText={t('participants.addDialog.searchHint')}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>{t('common:actions.cancel')}</Button>
                <Button variant="contained" onClick={add} disabled={busy}>{t('common:actions.add')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddParticipantsDialog;
